// frontend/src/components/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { FaGamepad, FaAngleDown, FaAngleUp } from 'react-icons/fa'; // Added FaAngleDown, FaAngleUp

// Read Backend URL from environment variable - CRITICAL for deployment
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;
if (!BACKEND_API_URL) {
    // Log a critical error if the environment variable is missing.
    console.error("CRITICAL CONFIG ERROR: REACT_APP_BACKEND_API_URL environment variable is not set.");
}

// Constants for tile content display
const USERS_TO_SHOW_WHEN_COLLAPSED = 3; // Approx 2-3 lines of users
const CHARS_TO_SHOW_WHEN_COLLAPSED = 100; // Approx 2 lines of text

const LONG_SAMPLE_TEXT_TILE_2 = "This is a longer piece of sample text for the second tile to demonstrate the collapse and expand functionality. It needs to be substantial enough to clearly show that the content is being truncated when the tile is collapsed. We can add even more sentences here to ensure it properly overflows the initial two content lines. Let's make sure this text is sufficiently verbose. More text, more details, more demonstration of the feature. This should be enough to trigger the collapse button and show truncation.";

/**
 * Renders the main dashboard page for authenticated users.
 * Fetches user data and linked game usernames upon mounting.
 * Displays this information within the shared Layout.
 * Handles loading states and redirects unauthenticated users to the login page.
 * @returns {React.ReactElement} The rendered Dashboard component.
 */
function Dashboard() {
  // State for authenticated user's data
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Combined loading for initial user fetch

  // State for linked game users
  const [linkedGameUsers, setLinkedGameUsers] = useState([]);
  const [loadingLinkedUsers, setLoadingLinkedUsers] = useState(true);
  const [linkedUsersError, setLinkedUsersError] = useState(null);

  // State for tile expansion - default to true (expanded)
  const [isGameUsersExpanded, setIsGameUsersExpanded] = useState(true);
  const [isTile2Expanded, setIsTile2Expanded] = useState(true);
  const [isTile3Expanded, setIsTile3Expanded] = useState(true);

  // React Router hook for programmatic navigation
  const navigate = useNavigate();

  // Toggle functions for each tile
  const toggleGameUsersExpansion = () => setIsGameUsersExpanded(prev => !prev);
  const toggleTile2Expansion = () => setIsTile2Expanded(prev => !prev);
  const toggleTile3Expansion = () => setIsTile3Expanded(prev => !prev);

  /**
   * Fetches the main authenticated user's data from the backend.
   */
  const fetchUser = useCallback(async () => {
    if (!BACKEND_API_URL) {
        console.error("[Dashboard - User] Cannot fetch user data: REACT_APP_BACKEND_API_URL is not configured.");
        setLoading(false); // Stop initial loading
        navigate('/login'); // Redirect if backend URL is missing
        return;
    }

    setLoading(true); // Start initial loading
    try {
      const apiUrl = `${BACKEND_API_URL}/auth/me`;
      console.log(`[Dashboard - User] Fetching user data from: ${apiUrl}`);
      const response = await fetch(apiUrl, { credentials: 'include' });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('[Dashboard - User] User not authenticated (401).');
        } else {
          console.error(`[Dashboard - User] Error fetching user data: ${response.status} ${response.statusText}`);
        }
        throw new Error(`Authentication check failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData.success && responseData.user) {
          setUser(responseData.user);
          console.log('[Dashboard - User] User data received:', responseData.user);
      } else {
           console.error('[Dashboard - User] Backend response error or missing user data. Response:', responseData);
           throw new Error('Invalid user data format received from server.');
      }
    } catch (error) {
      console.error("[Dashboard - User] Auth check/fetch failed:", error.message);
      navigate('/login');
    } finally {
      setLoading(false); // Finish initial loading
    }
  }, [navigate]);

  /**
   * Fetches linked game usernames for the authenticated user.
   */
  const fetchLinkedGameUsers = useCallback(async () => {
    // Ensure user data is available and backend URL is configured
    if (!user || !BACKEND_API_URL) {
        if (!user) console.log("[Dashboard - LinkedUsers] User data not yet available, skipping fetch of linked users.");
        else console.error("[Dashboard - LinkedUsers] Cannot fetch linked users: REACT_APP_BACKEND_API_URL is not configured.");
        setLoadingLinkedUsers(false);
        return;
    }

    setLoadingLinkedUsers(true);
    setLinkedUsersError(null);
    try {
        const apiUrl = `${BACKEND_API_URL}/api/user/linked-game-accounts`;
        console.log(`[Dashboard - LinkedUsers] Fetching linked game users from: ${apiUrl}`);
        const response = await fetch(apiUrl, { credentials: 'include' });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
            throw new Error(errorData.message || `Failed to fetch linked game users: ${response.status}`);
        }

        const responseData = await response.json();
        if (responseData.success && Array.isArray(responseData.gameUsernames)) {
            setLinkedGameUsers(responseData.gameUsernames);
            console.log('[Dashboard - LinkedUsers] Linked game users received:', responseData.gameUsernames);
        } else {
            throw new Error(responseData.message || 'Invalid linked game users data format received from server.');
        }
    } catch (error) {
        console.error("[Dashboard - LinkedUsers] Fetch failed:", error.message);
        setLinkedUsersError(error.message);
        setLinkedGameUsers([]);
    } finally {
        setLoadingLinkedUsers(false);
    }
  }, [user]); // Depends on 'user' state

  // Effect for initial user data fetch
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Effect to fetch linked game users once user data is available
  useEffect(() => {
    if (user) { // Only fetch if user is loaded
        fetchLinkedGameUsers();
    }
  }, [user, fetchLinkedGameUsers]);

  // --- Conditional Rendering for Loading/Unauthenticated States ---
  if (loading) { // Initial loading for user data
    return (
      <Layout>
        <div className="flex justify-center items-center h-full">
          Loading dashboard...
        </div>
      </Layout>
    );
  }

  if (!user) { // If user fetch failed or user is null after loading
     return (
        <Layout>
          <div className="flex justify-center items-center h-full">
            Verifying authentication...
          </div>
        </Layout>
     );
  }

  // --- Render Dashboard Content ---
  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto text-center p-4"> {/* Adjusted max-width and padding for wider layout */}
        <h1 className="text-3xl font-bold mb-6">Welcome to Exodus!</h1>

        {/* Grid for Dashboard Tiles - 1 column on mobile, 3 columns on medium screens and up */}
        <div className="grid grid-cols-1 md:grid-cols-3 md:items-start gap-6 mb-6 text-left">
            {/* Game Users Tile */}
            <div className="bg-gray-800 bg-opacity-75 p-6 rounded-lg shadow-md min-h-[8rem] flex flex-col">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-xl font-semibold flex items-center">
                  <FaGamepad className="mr-2" /> Game Users
                </h2>
                {!loadingLinkedUsers && !linkedUsersError && linkedGameUsers.length > USERS_TO_SHOW_WHEN_COLLAPSED && (
                  <button
                    onClick={toggleGameUsersExpansion}
                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center focus:outline-none"
                    aria-expanded={isGameUsersExpanded}
                  >
                    {isGameUsersExpanded ? 'Collapse' : 'Expand'}
                    {isGameUsersExpanded ? <FaAngleUp className="ml-1" /> : <FaAngleDown className="ml-1" />}
                  </button>
                )}
              </div>

              {/* Scrollable content area for game users */}
              <div className="flex-grow overflow-y-auto">
                {loadingLinkedUsers && <p className="text-gray-400 text-sm">Loading game users...</p>}
                {linkedUsersError && <p className="text-red-400 text-sm">Error: {linkedUsersError}</p>}
                {!loadingLinkedUsers && !linkedUsersError && (
                  linkedGameUsers.length > 0 ? (
                    <>
                      <ul className="list-disc list-inside text-gray-200 space-y-1 text-sm">
                        {(isGameUsersExpanded ? linkedGameUsers : linkedGameUsers.slice(0, USERS_TO_SHOW_WHEN_COLLAPSED))
                          .map((gameUser, index) => (
                            <li key={gameUser || index}>{gameUser}</li>
                        ))}
                      </ul>
                      {!isGameUsersExpanded && linkedGameUsers.length > USERS_TO_SHOW_WHEN_COLLAPSED && (
                        <p className="text-xs text-gray-400 mt-2 italic">
                          ... and {linkedGameUsers.length - USERS_TO_SHOW_WHEN_COLLAPSED} more.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm">No game users linked to this Discord account.</p>
                  )
                )}
              </div>
            </div>

            {/* Placeholder Tile 2 */}
            <div className="bg-gray-800 bg-opacity-75 p-6 rounded-lg shadow-md min-h-[8rem] flex flex-col">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-xl font-semibold flex items-center">
                  {/* <FaChartBar className="mr-2" /> */} Placeholder Tile 2
                </h2>
                {LONG_SAMPLE_TEXT_TILE_2.length > CHARS_TO_SHOW_WHEN_COLLAPSED && (
                  <button
                    onClick={toggleTile2Expansion}
                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center focus:outline-none"
                    aria-expanded={isTile2Expanded}
                  >
                    {isTile2Expanded ? 'Collapse' : 'Expand'}
                    {isTile2Expanded ? <FaAngleUp className="ml-1" /> : <FaAngleDown className="ml-1" />}
                  </button>
                )}
              </div>
              {/* Scrollable content area for tile 2 */}
              <div className="flex-grow overflow-y-auto">
                <p className="text-gray-300 text-sm">
                  {isTile2Expanded
                    ? LONG_SAMPLE_TEXT_TILE_2
                    : `${LONG_SAMPLE_TEXT_TILE_2.substring(0, CHARS_TO_SHOW_WHEN_COLLAPSED)}...`}
                </p>
              </div>
            </div>

            {/* Placeholder Tile 3 */}
            {(() => {
              const tile3Content = "Content for the third tile. This is shorter and might not always need a collapse button, depending on the CHARS_TO_SHOW_WHEN_COLLAPSED limit.";
              const showTile3Button = tile3Content.length > CHARS_TO_SHOW_WHEN_COLLAPSED;
              return (
                <div className="bg-gray-800 bg-opacity-75 p-6 rounded-lg shadow-md min-h-[8rem] flex flex-col">
                  <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-semibold">Placeholder Tile 3</h2>
                    {showTile3Button && (
                      <button
                        onClick={toggleTile3Expansion}
                        className="text-sm text-blue-400 hover:text-blue-300 flex items-center focus:outline-none"
                        aria-expanded={isTile3Expanded}
                      >
                        {isTile3Expanded ? 'Collapse' : 'Expand'}
                        {isTile3Expanded ? <FaAngleUp className="ml-1" /> : <FaAngleDown className="ml-1" />}
                      </button>
                    )}
                  </div>
                  {/* Scrollable content area for tile 3 */}
                  <div className="flex-grow overflow-y-auto">
                    <p className="text-gray-300 text-sm">
                      {isTile3Expanded || !showTile3Button
                        ? tile3Content
                        : `${tile3Content.substring(0, CHARS_TO_SHOW_WHEN_COLLAPSED)}...`}
                    </p>
                  </div>
                </div>
              );
            })()}
        </div>
        {/* Additional dashboard content can be added here */}
      </div>
    </Layout>
  );
}

export default Dashboard;
